<template lang="pug">
  div(class="document-event-log")
    div(class="document-event-log__header")
      h5(class="text-14px dark fw-500") {{ $t('Event log') }}
        v-btn(
          v-if="showClose"
          class="document-event-log__close-btn"
          icon
        )
          e-svg-icon(
            size="sm"
            @click="close"
          ) close
    div(
      v-if="loading"
      class="pa-3"
    )
      v-skeleton-loader(
        v-for="(skeleton, idx) in 3"
        :key="idx"
        type="card-heading, paragraph"
      )
    div(
      v-else-if="!loading"
      class="document-event-log__list"
    )
      div(
        class="document-event-log__item"
        v-for="event in events"
        :key="event.id"
      )
        div(class="document-event-log__item-status")
          e-svg-icon(:name="getIconNameByAction(event.action)")
        div(class="document-event-log__item-content")
          div(class="document-event-log__item-text")
            span(
              class="text-12px light"
              :class="getTextClasByAction(event)"
            ) {{ getTextByAction(event) }}
            div(class="text-12px dark") {{ getUserNameOrEmail(event) }}
          div(class="document-event-log__item-info")
            e-svg-icon(
              size="sm"
              :name="isMobile(event.userAgent) ? 'mobile' : 'monitor'"
              class="document-event-log__item-info-icon"
            )
            div(class="text-10px light pl-2") {{ formatDate(event.createdAt) }}
</template>

<script>
import documentSidebar from '~/modules/documents/mixins/documentSidebar'

const ACTIONS = {
  VIEWED: 'document_viewed',
  APPROVED: 'document_approved',
  SIGNED: 'document_signed',
  REJECTED: 'document_rejected'
}

const actionTexts = {
  [ACTIONS.VIEWED]: { text: 'Прочитано', class: '' },
  [ACTIONS.APPROVED]: { text: 'Погоджено', class: 'green-text' },
  [ACTIONS.SIGNED]: { text: 'Підписано', class: 'green-text' },
  [ACTIONS.REJECTED]: { text: 'Відхилено', class: 'red-text' }
}

export default {
  name: 'DocumentEventLog',
  mixins: [documentSidebar],
  props: {
    document: {
      type: Object,
      default: null
    },
    showClose: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      loading: false,
      events: []
    }
  },

  created() {
    this.fetchLogs()
  },
  methods: {
    async fetchLogs() {
      try {
        this.loading = true
        const request = await this.$store.dispatch('documents/logs', this.document.id)
        const data = this._.get(request, 'data')
        this.events = data.data
      } catch (e) {
        this.$handlers.error(e, this)
      } finally {
        this.loading = false
      }
    },
    getIconNameByAction(action) {
      switch (action) {
        case ACTIONS.VIEWED:
          return 'eye'

        case ACTIONS.APPROVED:
          return 'check-circle-success-with-border'

        case ACTIONS.SIGNED:
          return 'sign'

        case ACTIONS.REJECTED:
          return 'close-circle'

        default:
          return 'clock'
      }
    },
    getTextClasByAction(event) {
      return this._.get(actionTexts[event.action], 'class', '')
    },
    getTextByAction(event) {
      return this._.get(actionTexts[event.action], 'text', '')
    },
    getUserNameOrEmail(event) {
      const { user } = event
      return user?.name || user?.email || this.$t('Unauthorized user')
    },

    formatDate(date) {
      const months = [
        'Січня',
        'Лютого',
        'Березня',
        'Квітня',
        'Травня',
        'Червня',
        'Липня',
        'Серпня',
        'Вересня',
        'Жовтня',
        'Листопада',
        'Грудня'
      ]

      const d = new Date(date)
      const day = d.getDate()
      const month = months[d.getMonth()]
      const year = d.getFullYear()

      const hours = d.getHours().toString().padStart(2, '0')
      const minutes = d.getMinutes().toString().padStart(2, '0')

      return `${day} ${month} ${year}, ${hours}:${minutes}`
    },

    isMobile(userAgent) {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)
    },
    close() {
      this.documentSidebar.hide()
    }
  }
}
</script>

<style scoped lang="scss">
.document-event-log {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &__header {
    padding: 14px 12px;
    border-bottom: 0.5px solid var(--stroke-terteriary, #e3e3e3);
    position: relative;
  }

  &__list {
    flex-grow: 1;
    overflow-y: auto;
  }

  &__item {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 10px;
    padding: 8px 12px;

    position: relative;

    &:not(:last-child) {
      &:before {
        content: '';
        width: 1px;
        height: calc(100% + 10px);

        background: #f1f1f1;

        position: absolute;
        top: 8px;
        left: 24px;
        z-index: map-get($zIndexes, 'base');
      }
    }
  }
  &__item-status {
    z-index: map-get($zIndexes, 'middle');
    width: 24px;
    min-width: 24px;
    height: 24px;
    border: 0.5px solid #d4d4d4;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;

    > svg.icon {
      width: 12px;
      height: 12px;
    }
  }
  &__item-text {
    > span {
      padding-right: 8px;
      word-break: break-word;
      overflow-wrap: break-word;
    }
    > .green-text {
      color: $success;
    }
    > .red-text {
      color: $danger;
    }
  }

  &__item-content {
    display: flex;
    flex-direction: column;
    gap: 6px;
    align-items: flex-start;
    align-self: stretch;
  }

  &__close-btn {
    width: 32px;
    height: 32px;

    position: absolute;
    top: 50%;
    right: 4px;
    transform: translateY(-50%);

    z-index: map-get($zIndexes, 'base');
  }

  &__item-info {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &__item-info-icon {
    width: 12px;
    height: 12px;
  }
}
</style>
